/**
 * Cache API client extension for TestDino API
 */

import type { Config } from '@/config';
import { NetworkError, AuthenticationError } from '@/types';
import { withRetry } from '@/utils/retry';
import { VERSION } from '@/version';
import { logger } from '@/utils/logger';

/**
 * Cache submission response from TestDino API
 */
export interface CacheSubmissionResponse {
  success: boolean;
  cacheId: string;
  message?: string | undefined;
}

/**
 * Cache retrieval options
 */
export interface CacheRetrievalOptions {
  cacheId: string;
  shardIndex?: number;
  forAllShards?: boolean;
}

/**
 * Cache retrieval response - Default (no query params)
 */
export interface CacheRetrievalResponse {
  success: boolean;
  message: string;
  data?: {
    cacheId: string;
    pipelineId: string;
    commit: string;
    cache: {
      branch: string;
      commit: string;
      repository: string;
      ci: {
        provider: string;
        pipelineId: string;
        buildNumber: string;
      };
      failures: Array<{
        file: string;
        testTitle: string;
      }>;
      // Optional fields based on query params
      shardInfo?: {
        isSharded: boolean;
        shardIndex: number;
        shardTotal: number;
      };
      shardTotal?: number;
      shards?: Array<{
        shardIndex: number;
        failures: Array<{
          file: string;
          testTitle: string;
        }>;
      }>;
    };
  };
}

/**
 * Cache payload structure matching new API specification
 */
export interface CachePayload {
  cacheId: string; // Format: ciProvider_repoName_branch
  pipelineId: string; // CI pipeline/run ID
  commit: string; // Git commit hash
  branch: string; // Original branch name
  repository: string; // Full repo (owner/repo)
  ci: {
    provider: string;
    pipelineId: string;
    buildNumber: string;
  };
  isSharded: boolean;
  shardIndex: number | null;
  shardTotal: number | null;
  failures: Array<{
    file: string;
    testTitle: string;
    project?: string; // Optional for backward compatibility
  }>;
  summary: {
    total: number;
    passed: number;
    failed: number;
    skipped: number;
    duration: number;
  };
  timestamp: string;
}

/**
 * Cache API client for submitting test metadata to TestDino
 */
export class CacheApiClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor(config: Config) {
    this.baseUrl = config.apiUrl;
    this.apiKey = config.token;
  }

  /**
   * Submit cache data to TestDino API with retry logic
   */
  async submitCacheData(
    payload: CachePayload
  ): Promise<CacheSubmissionResponse> {
    return await withRetry(async () => this.submitCacheDataAttempt(payload), {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 5000,
    });
  }

  /**
   * Single attempt to submit cache data
   */
  private async submitCacheDataAttempt(
    payload: CachePayload
  ): Promise<CacheSubmissionResponse> {
    const url = `${this.baseUrl}/api/cache`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        await this.handleHttpError(response);
      }

      const data = await response.json();
      return this.parseResponse(data);
    } catch (error) {
      if (
        error instanceof AuthenticationError ||
        error instanceof NetworkError
      ) {
        throw error;
      }

      // Handle network-level errors
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw new NetworkError(`Failed to submit cache data: ${errorMessage}`);
    }
  }

  /**
   * Headers for API requests
   */
  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'User-Agent': `tdpw-cache/${VERSION}`,
      'X-API-Key': this.apiKey,
    };
  }

  /**
   * Handle HTTP error responses
   */
  private async handleHttpError(response: Response): Promise<never> {
    let errorBody: string;
    try {
      errorBody = await response.text();
    } catch {
      errorBody = 'Unable to read error response';
    }

    switch (response.status) {
      case 401:
        throw new AuthenticationError('Invalid API key for cache submission');

      case 403:
        throw new AuthenticationError(
          'API key does not have permission to submit cache data'
        );

      case 400:
        throw new NetworkError(`Bad cache data format: ${errorBody}`);

      case 404:
        throw new NetworkError(
          'Cache endpoint not found - feature may not be available yet'
        );

      case 409: {
        // Conflict - cache data already exists (not an error, don't retry)
        // This is a non-retryable error - mark it specially so retry logic stops
        const conflictError = new NetworkError(
          'Cache data already exists for this shard'
        );
        (conflictError as NetworkError & { retryable: boolean }).retryable =
          false;
        throw conflictError;
      }

      case 413:
        throw new NetworkError(
          'Cache payload too large - consider reducing failure data'
        );

      case 429:
        throw new NetworkError('Rate limit exceeded for cache submissions');

      case 500:
      case 502:
      case 503:
      case 504:
        throw new NetworkError(
          `Server error submitting cache data (${response.status}) - will retry`
        );

      default:
        throw new NetworkError(
          `Cache submission failed: HTTP ${response.status} - ${errorBody}`
        );
    }
  }

  /**
   * Parse and validate API response
   */
  private parseResponse(data: unknown): CacheSubmissionResponse {
    if (!data || typeof data !== 'object') {
      throw new NetworkError('Invalid cache submission response format');
    }

    const response = data as Record<string, unknown>;

    // Handle response with nested data structure
    if (
      'success' in response &&
      'data' in response &&
      typeof response.data === 'object'
    ) {
      const responseData = response.data as Record<string, unknown>;
      return {
        success: Boolean(response.success),
        cacheId: String(responseData.cacheId || 'unknown'),
        message: response.message ? String(response.message) : undefined,
      };
    }

    // Handle flat response format
    if ('success' in response && 'cacheId' in response) {
      return {
        success: Boolean(response.success),
        cacheId: String(response.cacheId || 'unknown'),
        message: response.message ? String(response.message) : undefined,
      };
    }

    // Fallback: assume success if we got a response
    return {
      success: true,
      cacheId: response.cacheId ? String(response.cacheId) : 'unknown',
      message: response.message ? String(response.message) : undefined,
    };
  }

  /**
   * Test if cache API endpoint is available (health check)
   */
  async testCacheEndpoint(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/cache`, {
        method: 'OPTIONS', // Preflight request
        headers: {
          'User-Agent': `tdpw-cache/${VERSION}`,
        },
      });

      // Even 404 means the server is responding
      return response.status < 500;
    } catch {
      return false;
    }
  }

  /**
   * Mock cache submission for development/testing
   */
  async mockCacheSubmission(
    payload: CachePayload
  ): Promise<CacheSubmissionResponse> {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    logger.debug(
      `MOCK: Would submit cache data to /api/v1/cache: cacheId=${payload.cacheId}, pipelineId=${payload.pipelineId}, commit=${payload.commit}, isSharded=${payload.isSharded}, shardIndex=${payload.shardIndex}, shardTotal=${payload.shardTotal}, failures=${payload.failures.length}, totalTests=${payload.summary.total}`
    );

    return {
      success: true,
      cacheId: payload.cacheId,
      message: payload.isSharded
        ? `Mock cache submission successful for shard ${payload.shardIndex}/${payload.shardTotal}`
        : 'Mock cache submission successful',
    };
  }

  /**
   * Retrieve cache data for a specific cache ID
   */
  async retrieveCache(
    options: CacheRetrievalOptions
  ): Promise<CacheRetrievalResponse> {
    // Validate mutually exclusive parameters
    if (options.shardIndex !== undefined && options.forAllShards) {
      throw new Error(
        'Cannot specify both shardIndex and forAllShards parameters'
      );
    }

    const url = new URL(`${this.baseUrl}/api/cache/${options.cacheId}`);

    // Add query parameters
    if (options.shardIndex !== undefined) {
      url.searchParams.append('shardIndex', options.shardIndex.toString());
    }
    if (options.forAllShards === true) {
      url.searchParams.append('forAllShards', 'true');
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        return this.handleRetrievalError(response, options.cacheId);
      }

      const data = (await response.json()) as CacheRetrievalResponse;
      return data;
    } catch (error) {
      // Network or parsing error
      return {
        success: false,
        message: `Failed to retrieve cache: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * Handle retrieval errors with appropriate messages
   */
  private async handleRetrievalError(
    response: Response,
    cacheId: string
  ): Promise<CacheRetrievalResponse> {
    let errorMessage = 'Failed to retrieve cache data';
    let errorBody: { message?: string } | undefined;

    try {
      errorBody = (await response.json()) as { message?: string };
      errorMessage = errorBody.message || errorMessage;
    } catch {
      // If response body is not JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }

    switch (response.status) {
      case 400:
        return {
          success: false,
          message:
            errorMessage ||
            'Invalid request: Cannot specify both shardIndex and forAllShards',
        };

      case 401:
      case 403:
        return {
          success: false,
          message: 'Unauthorized: Invalid or missing API token',
        };

      case 404:
        return {
          success: false,
          message: `Cache not found for cache ID: ${cacheId}`,
        };

      default:
        return {
          success: false,
          message: `${errorMessage} (HTTP ${response.status})`,
        };
    }
  }

  /**
   * Get cache data for specific cache ID (simplified for last-failed command)
   */
  async getCacheData(
    cacheId: string,
    options?: {
      branch?: string;
      commit?: string;
      shard?: number;
      pipelineId?: string;
    }
  ): Promise<{
    cacheId: string;
    failures: Array<{ file: string; testTitle: string }>;
    branch: string;
    repository: string;
  } | null> {
    try {
      const url = new URL(`${this.baseUrl}/api/cache/${cacheId}`);

      // Add query parameters if provided
      if (options?.branch) {
        url.searchParams.append('branch', options.branch);
      }
      if (options?.commit) {
        url.searchParams.append('commit', options.commit);
      }
      if (options?.shard !== undefined) {
        url.searchParams.append('shard', options.shard.toString());
      }
      if (options?.pipelineId) {
        url.searchParams.append('pipelineId', options.pipelineId);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        // Return null for 404 or other errors - let caller handle fallback
        return null;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (await response.json()) as any;

      // Extract the data we need for last-failed command
      if (data?.data?.cache) {
        return {
          cacheId: data.data.cacheId || cacheId,
          failures: data.data.cache.failures || [],
          branch: data.data.cache.branch || 'unknown',
          repository: data.data.cache.repository || 'unknown',
        };
      }

      return null;
    } catch (_error) {
      // Network error - return null to allow fallback
      return null;
    }
  }

  /**
   * Get latest cache data (with optional branch filter)
   */
  async getLatestCacheData(branch?: string): Promise<{
    cacheId: string;
    failures: Array<{ file: string; testTitle: string }>;
    branch: string;
    repository: string;
  } | null> {
    try {
      const url = new URL(`${this.baseUrl}/api/cache/latest`);

      if (branch) {
        url.searchParams.append('branch', branch);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        return null;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = (await response.json()) as any;

      // Extract the data we need for last-failed command
      if (data?.data?.cache) {
        return {
          cacheId: data.data.cacheId || 'unknown',
          failures: data.data.cache.failures || [],
          branch: data.data.cache.branch || 'unknown',
          repository: data.data.cache.repository || 'unknown',
        };
      }

      return null;
    } catch (_error) {
      return null;
    }
  }
}
